<template>
    <div class="container">
        <header class="page-header">
            <h2 class="heading">{{ $t('modals.refer_patient.buttons.refer') }}</h2>
            <div class="main">
                <div>
                    <h3 class="heading_title">
                        {{ $t('modals.patient_profile.profile.detail_form.refer_patient_tab.heading') }}
                    </h3>
                    <p class="subtitle">{{ $t('modals.patient_profile.profile.detail_form.refer_patient_tab.subheading') }}</p>
                </div>
            </div>
        </header>

        <div class="refer-cards-container">
            <article class="refer-card refer-card--clickable refer-card--direct" @click="handleCardClick('direct')">
                <div class="icon-wrapper icon-wrapper--direct">
                    <common-icon name="refer" size="extra-huge"></common-icon>
                </div>
                <div class="header">
                    <h3 class="title">{{ $t('layouts.app.topnav.direct_referral') }}</h3>
                    <p class="description">
                        {{ $t('modals.patient_profile.profile.detail_form.refer_patient_tab.direct_description') }}
                    </p>
                </div>
            </article>

            <article class="refer-card refer-card--clickable refer-card--match" @click="handleCardClick('match')">
                <div class="icon-wrapper icon-wrapper--match">
                    <common-icon name="users" size="extra-huge"></common-icon>
                </div>
                <div class="header">
                    <h3 class="title">{{ $t('layouts.app.topnav.match_referral') }}</h3>
                    <p class="description">
                        {{ $t('layouts.app.topnav.match_referral_description') }}.
                        {{ $t('modals.patient_profile.profile.detail_form.refer_patient_tab.match_description') }}
                    </p>
                </div>
            </article>

            <article
                v-if="!isUserPracticeOther"
                class="refer-card refer-card--clickable refer-card--eprescribe"
                @click="openEprescribe"
            >
                <div class="icon-wrapper icon-wrapper--eprescribe">
                    <common-icon name="navigation" size="extra-huge"></common-icon>
                </div>
                <div class="header">
                    <h3 class="title">{{ $t('layouts.app.topnav.eprescribe') }}</h3>
                    <p class="description">
                        {{ $t('layouts.app.topnav.eprescribe_description') }}.
                        {{ $t('modals.patient_profile.profile.detail_form.refer_patient_tab.epres_description') }}
                    </p>
                </div>
            </article>
        </div>

        <div class="security-note">
            <span class="security-note__icon">
                <common-icon name="verify" size="small"></common-icon>
            </span>
            <span>{{ $t('modals.patient_profile.profile.detail_form.refer_patient_tab.security_note') }}</span>
        </div>

        <common-modal v-if="isSelectionModalOpen" width="large" :showCloseButton="true" @close="closeSelectionModal">
            <div class="selection-modal">
                <div class="selection-modal__header">
                    <div class="selection-modal__badge" :class="modalBadgeClass">
                        <common-icon :name="modalBadgeIcon" size="small"></common-icon>
                        <span>{{ modalBadgeLabel }}</span>
                    </div>
                    <h3 class="selection-modal__title">{{ modalTitle }}</h3>
                    <p class="selection-modal__subtitle">{{ modalSubtitle }}</p>
                </div>

                <div class="selection-modal__options">
                    <div
                        class="selection-option-card"
                        :class="selectionOptionCardClass"
                        @click="handleOptionSelect(PracticeTypeEnum.PHYSICIAN)"
                    >
                        <div class="selection-option-card__icon">
                            <common-icon name="aala" size="large"></common-icon>
                        </div>
                        <div class="selection-option-card__content">
                            <div class="selection-option-card__badge">{{ $t('layouts.app.topnav.provider_direct') }}</div>
                            <h4 class="selection-option-card__title">
                                {{ $t('modals.patient_profile.profile.detail_form.refer_patient_tab.provider_title') }}
                            </h4>
                            <p class="selection-option-card__desc">
                                {{ providerOptionDescription }}
                            </p>
                        </div>
                        <button type="button" class="selection-option-card__btn">
                            <span>{{ providerOptionBtnText }}</span>
                            <common-icon name="arrow-right" size="tiny"></common-icon>
                        </button>
                    </div>

                    <div
                        class="selection-option-card"
                        :class="selectionOptionCardClass"
                        @click="handleOptionSelect(PracticeTypeEnum.ANCILLARY)"
                    >
                        <div class="selection-option-card__icon">
                            <common-icon name="building" size="large"></common-icon>
                        </div>
                        <div class="selection-option-card__content">
                            <div class="selection-option-card__badge">{{ $t('layouts.app.topnav.direct_ancillary') }}</div>
                            <h4 class="selection-option-card__title">
                                {{ $t('modals.patient_profile.profile.detail_form.refer_patient_tab.ancillary_title') }}
                            </h4>
                            <p class="selection-option-card__desc">
                                {{ ancillaryOptionDescription }}
                            </p>
                        </div>
                        <button type="button" class="selection-option-card__btn">
                            <span>{{ ancillaryOptionBtnText }}</span>
                            <common-icon name="arrow-right" size="tiny"></common-icon>
                        </button>
                    </div>
                </div>
            </div>
        </common-modal>
    </div>
</template>

<script>
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'

    export default {
        props: {
            patientDetails: {
                type: Object,
                default: () => ({}),
            },
        },

        data() {
            return {
                PracticeTypeEnum,
                isSelectionModalOpen: false,
                selectedReferralMode: 'direct', // 'direct' | 'match'
            }
        },

        computed: {
            patientId() {
                return this.patientDetails?.id || this.$route.params.id || null
            },

            isUserPracticeOther() {
                return this.$auth.isUserPracticeOther()
            },

            modalBadgeClass() {
                return `selection-modal__badge--${this.selectedReferralMode}`
            },

            modalBadgeIcon() {
                return this.selectedReferralMode === 'direct' ? 'refer' : 'users'
            },

            modalBadgeLabel() {
                return this.selectedReferralMode === 'direct'
                    ? this.$t('layouts.app.topnav.direct_referral')
                    : this.$t('layouts.app.topnav.match_referral')
            },

            modalTitle() {
                return this.selectedReferralMode === 'direct'
                    ? this.$t('modals.patient_profile.profile.detail_form.refer_patient_tab.modal_direct_title')
                    : this.$t('modals.patient_profile.profile.detail_form.refer_patient_tab.modal_match_title')
            },

            modalSubtitle() {
                return this.selectedReferralMode === 'direct'
                    ? this.$t('modals.patient_profile.profile.detail_form.refer_patient_tab.modal_direct_subtitle')
                    : this.$t('modals.patient_profile.profile.detail_form.refer_patient_tab.modal_match_subtitle')
            },

            selectionOptionCardClass() {
                return `selection-option-card--${this.selectedReferralMode}`
            },

            providerOptionDescription() {
                return this.selectedReferralMode === 'direct'
                    ? this.$t('modals.patient_profile.profile.detail_form.refer_patient_tab.provider_desc')
                    : this.$t('modals.patient_profile.profile.detail_form.refer_patient_tab.match_provider_desc')
            },

            providerOptionBtnText() {
                return this.selectedReferralMode === 'direct'
                    ? this.$t('layouts.app.topnav.direct_physican')
                    : this.$t('layouts.app.topnav.match_physican')
            },

            ancillaryOptionDescription() {
                return this.selectedReferralMode === 'direct'
                    ? this.$t('modals.patient_profile.profile.detail_form.refer_patient_tab.ancillary_desc')
                    : this.$t('modals.patient_profile.profile.detail_form.refer_patient_tab.match_ancillary_desc')
            },

            ancillaryOptionBtnText() {
                return this.selectedReferralMode === 'direct'
                    ? this.$t('layouts.app.topnav.direct_ancillary')
                    : this.$t('layouts.app.topnav.match_ancillary')
            },
        },

        methods: {
            handleCardClick(mode) {
                this.selectedReferralMode = mode
                this.isSelectionModalOpen = true
            },

            closeSelectionModal() {
                this.isSelectionModalOpen = false
            },

            handleOptionSelect(practiceType) {
                this.closeSelectionModal()
                if (this.selectedReferralMode === 'direct') {
                    this.openDirectReferral(practiceType)
                } else if (this.selectedReferralMode === 'match') {
                    this.openMatchReferral(practiceType)
                }
            },

            openDirectReferral(practiceType) {
                this.emitEvent({ name: 'open-direct-referral', payload: practiceType })
            },

            openMatchReferral(practiceType) {
                const query = { practiceType }

                if (this.patientId) {
                    query.patientId = this.patientId
                }

                this.$router.push({
                    name: 'pool.new',
                    query,
                })
            },

            openEprescribe() {
                const query = {}
                if (this.patientId) {
                    query.patientId = this.patientId
                }
                this.$router.push({
                    name: 'referrals.eprescribe',
                    query,
                })
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';

    .heading {
        font-size: 16px;
        color: oklch(0.21 0.034 264.665);
        font-weight: 500;
        margin-bottom: 14px;
    }
    .heading_title {
        font-size: 18px;
        color: oklch(0.21 0.034 264.665);
        font-weight: 500;
    }
    .subtitle {
        color: #666;
        margin-bottom: 16px;
        font-size: 12px;
        font-weight: 400;
    }
    .main {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .refer-cards-container {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 18px;
        align-items: stretch;
        margin-top: 16px;
    }

    .refer-card {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex-direction: column;
        min-height: 220px;
        padding: 32px 24px 28px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 14px;
        transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;

        &--clickable {
            cursor: pointer;

            &:hover {
                transform: translateY(-4px);
                box-shadow: 0 14px 28px -6px rgba(0, 0, 0, 0.09), 0 4px 10px -4px rgba(0, 0, 0, 0.04);

                .icon-wrapper {
                    transform: scale(1.08);
                }
            }
        }

        &--direct:hover {
            border-color: #93c5fd;
        }

        &--match:hover {
            border-color: #c4b5fd;
        }

        &--eprescribe:hover {
            border-color: #6ee7b7;
        }
    }

    .header {
        display: flex;
        align-items: center;
        flex-direction: column;
        gap: 10px;
        margin-top: 18px;
        text-align: center;
    }

    .icon-wrapper {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 80px;
        height: 80px;
        border-radius: 50%;
        border: 1px solid;
        transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);

        &--direct {
            background: #eff6ff;
            border-color: #dbeafe;
            color: #2563eb;

            i {
                color: inherit !important;
            }
        }

        &--match {
            background: #f5f3ff;
            border-color: #e9d5ff;
            color: #7c3aed;

            i {
                color: inherit !important;
            }
        }

        &--eprescribe {
            background: #ecfdf5;
            border-color: #d1fae5;
            color: #059669;

            i {
                color: inherit !important;
            }
        }
    }

    .title {
        font-size: 18px;
        font-weight: 600;
        color: #0f172a;
        margin: 0;
    }

    .description {
        margin: 0;
        color: #64748b;
        font-size: 13px;
        line-height: 1.55;
        text-align: center;
    }

    .security-note {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 20px;
        padding: 16px 20px;
        border-radius: 10px;
        border: 1px solid #e2e8f0;
        background: #ffffff;
        color: #475569;
        font-size: 13px;
        font-weight: 500;
        line-height: 1.5;
    }

    .security-note__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        flex-shrink: 0;
        border-radius: 50%;
        background: #e0f2fe;

        i {
            font-size: 14px;
            color: #0369a1 !important;
        }
    }

    /* Modal Styles */
    .selection-modal {
        padding: 10px 6px 14px;

        &__header {
            text-align: center;
            margin-bottom: 24px;
        }

        &__badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 4px 10px;
            border-radius: 999px;
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 10px;

            &--direct {
                background: #eff6ff;
                color: #2563eb;
                border: 1px solid #dbeafe;
            }

            &--match {
                background: #f5f3ff;
                color: #7c3aed;
                border: 1px solid #e9d5ff;
            }
        }

        &__title {
            font-size: 20px;
            font-weight: 600;
            color: #0f172a;
            margin: 0 0 6px;
        }

        &__subtitle {
            font-size: 13px;
            color: #64748b;
            margin: 0 auto;
            max-width: 460px;
            line-height: 1.5;
        }

        &__options {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 16px;
        }
    }

    .selection-option-card {
        display: flex;
        flex-direction: column;
        padding: 24px 20px;
        border-radius: 12px;
        border: 1.5px solid #e2e8f0;
        background: #ffffff;
        cursor: pointer;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        text-align: left;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.08);
        }

        &--direct:hover {
            border-color: #3b82f6;
            background: #fafcff;

            .selection-option-card__btn {
                background: #2563eb;
                color: #ffffff;
                border-color: #2563eb;
            }
        }

        &--match:hover {
            border-color: #8b5cf6;
            background: #faf9ff;

            .selection-option-card__btn {
                background: #7c3aed;
                color: #ffffff;
                border-color: #7c3aed;
            }
        }

        &__icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 48px;
            height: 48px;
            border-radius: 10px;
            margin-bottom: 14px;
        }

        &--direct .selection-option-card__icon {
            background: #eff6ff;
            color: #2563eb;
        }

        &--match .selection-option-card__icon {
            background: #f5f3ff;
            color: #7c3aed;
        }

        &__badge {
            display: inline-block;
            font-size: 10px;
            font-weight: 600;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            margin-bottom: 4px;
        }

        &__title {
            font-size: 16px;
            font-weight: 600;
            color: #0f172a;
            margin: 0 0 6px;
        }

        &__desc {
            font-size: 12.5px;
            color: #64748b;
            line-height: 1.5;
            margin: 0 0 18px;
            flex: 1;
        }

        &__btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            width: 100%;
            padding: 9px 14px;
            border-radius: 8px;
            border: 1px solid #cbd5e1;
            background: #f8fafc;
            color: #334155;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
            margin-top: auto;
        }
    }

    @media (max-width: 980px) {
        .refer-cards-container {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }

    @media (max-width: 640px) {
        .refer-cards-container {
            grid-template-columns: 1fr;
        }

        .refer-card {
            min-height: unset;
        }

        .selection-modal__options {
            grid-template-columns: 1fr;
        }
    }
</style>
